import React from 'react';

/**
 * Informação de progresso detalhado (vindo do C# progress_update)
 */
export interface DetailedProgress {
  /** Percentual completo (0-100) */
  percentComplete: number;
  /** Mensagem descritiva */
  message: string;
  /** Número atual de items processados */
  current: number;
  /** Total de items a processar */
  total: number;
}

interface PreparationOverlayProps {
  visible: boolean;
  steps: Array<{ id: string; label: string; description?: string }>;
  currentStep: string;
  error: string | null;
  onClose?: () => void;
  onRetry?: () => void;
  title?: string;
  /** Mensagem de retry (ex: "Tentando novamente (2/3)...") */
  retryMessage?: string | null;
  /** Progresso detalhado da etapa atual (ex: coleta de metadados do C#) */
  detailedProgress?: DetailedProgress | null;
  /** Progresso global simulado (0-100) - barra de progresso na parte inferior */
  progress?: number;
}

export function PreparationOverlay({
  visible,
  steps,
  currentStep,
  error,
  onClose,
  onRetry,
  title,
  retryMessage,
  detailedProgress,
  progress
}: PreparationOverlayProps) {
  if (!visible) return null;

  const currentIndex = steps.findIndex(s => s.id === currentStep);
  
  const getStatus = (index: number) => {
    if (error && index === currentIndex) return 'error';
    if (currentIndex > index) return 'done';
    if (index === currentIndex) return 'active';
    return 'pending';
  };
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 transition-opacity duration-200">
      <div className="bg-navy-800 border border-navy-700 rounded-xl shadow-2xl w-full max-w-md sm:max-w-lg lg:max-w-xl p-5 transition-all duration-300">
        <h2 className="text-lg font-semibold text-white mb-1.5">{title || 'Preparando...'}</h2>
        <p className="text-xs text-gray-400 mb-5 leading-relaxed">Aguarde enquanto preparamos tudo para você.</p>
        
        <ul className="space-y-3.5">
          {steps.map((step, index) => {
            const status = getStatus(index);
            
            return (
              <li 
                key={step.id} 
                className={`flex items-start gap-3.5 transition-all duration-300 ease-in-out ${
                  status === 'active' ? 'opacity-100 translate-x-0' : 
                  status === 'done' ? 'opacity-80' : 
                  'opacity-45'
                }`}
              >
                {/* ✅ Indicador com animações melhoradas e sombras sutis */}
                <div className="relative flex-shrink-0 mt-0.5">
                  {status === 'done' && (
                    <div className="w-3.5 h-3.5 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/30 transition-all duration-300 ring-1 ring-emerald-500/20" />
                  )}
                  {status === 'active' && (
                    <div className="w-3.5 h-3.5 rounded-full border-2 border-yellow-300 border-t-transparent animate-spin shadow-sm shadow-yellow-300/20 ring-1 ring-yellow-400/10" 
                         style={{ animationDuration: '0.8s' }} />
                  )}
                  {status === 'error' && (
                    <div className="w-3.5 h-3.5 rounded-full bg-red-400 animate-pulse shadow-sm shadow-red-400/30 ring-1 ring-red-500/20" 
                         style={{ animationDuration: '1s' }} />
                  )}
                  {status === 'pending' && (
                    <div className="w-3 h-3 rounded-full bg-gray-500/60 transition-opacity duration-300" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className={`text-sm transition-all duration-300 ${
                    status === 'active' ? 'text-white font-semibold' : 
                    status === 'done' ? 'text-gray-300 font-medium' : 
                    status === 'error' ? 'text-red-300 font-medium' : 
                    'text-gray-500 font-normal'
                  }`}>
                    {step.label}
                  </div>
                  {status === 'active' && (
                    <div className="text-xs text-gray-400 mt-1.5 leading-relaxed transition-opacity duration-300 opacity-85 max-w-sm">
                      {retryMessage ? (
                        <span className="text-yellow-400 font-medium">{retryMessage}</span>
                      ) : detailedProgress ? (
                        <div className="space-y-2">
                          <span className="text-blue-300">{detailedProgress.message}</span>
                          {/* Barra de progresso */}
                          <div className="w-full bg-navy-700 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-blue-500 h-full rounded-full transition-all duration-300 ease-out"
                              style={{ width: `${Math.min(100, Math.max(0, detailedProgress.percentComplete))}%` }}
                            />
                          </div>
                          {/* Contador e percentual */}
                          <div className="flex justify-between text-[10px] text-gray-500">
                            <span>{detailedProgress.current} / {detailedProgress.total} objetos</span>
                            <span>{Math.round(detailedProgress.percentComplete)}%</span>
                          </div>
                        </div>
                      ) : (
                        step.description
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        
        {error ? (
          <div className="mt-4 space-y-3 transition-all duration-300">
            <div className="text-xs text-red-200 bg-red-500/10 border border-red-500/40 rounded-lg p-3 whitespace-pre-line leading-relaxed shadow-sm">
              {error}
            </div>
            <div className="flex gap-2">
              {onRetry && (
                <button 
                  onClick={onRetry} 
                  className="flex-1 px-3 py-2 text-sm rounded bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-medium transition-all duration-200 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
                >
                  Tentar Novamente
                </button>
              )}
              {onClose && (
                <button 
                  onClick={onClose} 
                  className={`${onRetry ? 'flex-1' : 'w-full'} px-3 py-2 text-sm rounded bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-white transition-all duration-200 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98]`}
                >
                  Fechar
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-5 pt-4 border-t border-navy-700/50 space-y-3">
            {/* Barra de progresso global simulada */}
            {typeof progress === 'number' && (
              <div className="space-y-1.5">
                <div className="w-full bg-navy-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-blue-400 h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                  />
                </div>
                <div className="flex justify-end">
                  <span className="text-[10px] text-gray-500 font-medium">{Math.round(progress)}%</span>
                </div>
              </div>
            )}
            <div className="text-xs text-gray-400/80 transition-opacity duration-300 flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-gray-400/50 animate-pulse" style={{ animationDuration: '1.5s' }} />
              <span>Este processo pode levar alguns instantes. Por favor, aguarde...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

